'use client';

import { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';

// --- Hooks ---
import { useTiptapEditor } from 'shared-components/hooks/use-tiptap-editor';

// --- Icons ---
import { HeadingIcon } from 'shared-components/components/TipTap/tiptap-icons/heading-icon';
import { NormalTextIcon } from 'shared-components/components/TipTap/tiptap-icons/normal-text-icon';

// --- Tiptap UI ---
import {
  headingIcons,
  isHeadingActive,
  canToggle,
  shouldShowButton,
} from 'shared-components/components/TipTap/tiptap-ui/heading-button';
import type { Level } from 'shared-components/components/TipTap/tiptap-ui/heading-button';

/**
 * Configuration for the heading dropdown menu functionality
 */
export interface UseHeadingDropdownMenuConfig {
  /**
   * The Tiptap editor instance.
   */
  editor?: Editor | null;
  /**
   * Available heading levels to show in the dropdown
   * @default [1, 2, 3, 4, 5, 6]
   */
  levels?: Level[];
  /**
   * Whether the dropdown should hide when headings are not available.
   * @default false
   */
  hideWhenUnavailable?: boolean;
  /**
   * Whether to show a "Normal text" option that converts the selection to paragraph.
   * @default false
   */
  showNormalText?: boolean;
}

/**
 * Gets the currently active heading level from the available levels
 */
export function getActiveHeadingLevel(editor: Editor | null, levels: Level[] = [1, 2, 3, 4, 5, 6]): Level | undefined {
  if (!editor || !editor.isEditable) return undefined;
  return levels.find((level) => isHeadingActive(editor, level));
}

/**
 * Custom hook that provides heading dropdown menu functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MyHeadingDropdown() {
 *   const {
 *     isVisible,
 *     activeLevel,
 *     isAnyHeadingActive,
 *     canToggle,
 *     levels,
 *   } = useHeadingDropdownMenu()
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <DropdownMenu>
 *       // dropdown content
 *     </DropdownMenu>
 *   )
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedHeadingDropdown() {
 *   const {
 *     isVisible,
 *     activeLevel,
 *   } = useHeadingDropdownMenu({
 *     editor: myEditor,
 *     levels: [1, 2, 3],
 *     hideWhenUnavailable: true,
 *   })
 *
 *   // component implementation
 * }
 * ```
 */
export function useHeadingDropdownMenu(config?: UseHeadingDropdownMenuConfig) {
  const {
    editor: providedEditor,
    levels = [1, 2, 3, 4, 5, 6],
    hideWhenUnavailable = false,
    showNormalText = false,
  } = config || {};

  const { editor } = useTiptapEditor(providedEditor);
  const [isVisible, setIsVisible] = useState(true);
  /** Incremented on selection change so activeLevel/isNormalText are recomputed and button updates. */
  const [selectionVersion, setSelectionVersion] = useState(0);

  const activeLevel = getActiveHeadingLevel(editor, levels);
  const isActive = isHeadingActive(editor);
  const canToggleState = canToggle(editor);
  /** True when showNormalText is on and selection is in a paragraph (not a heading). */
  const isNormalText = showNormalText && !activeLevel && !!editor?.isEditable && editor.isActive('paragraph');

  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      setSelectionVersion((v) => v + 1);
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable, level: levels }));
    };

    handleSelectionUpdate();

    editor.on('selectionUpdate', handleSelectionUpdate);

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
    };
  }, [editor, hideWhenUnavailable, levels]);

  return {
    isVisible,
    activeLevel,
    isActive,
    isNormalText,
    canToggle: canToggleState,
    levels,
    label: isNormalText ? 'Normal text' : activeLevel ? `Heading ${activeLevel}` : 'Heading',
    Icon: isNormalText ? NormalTextIcon : activeLevel ? headingIcons[activeLevel] : HeadingIcon,
  };
}
